import { useEffect, useRef, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { PocketPreview } from "@/components/PocketPreview";

/**
 * Public row of the three most-saved genre+BPM+key combos across all artists.
 * Tapping a pocket applies that filter combo. Hidden when fewer than 1 pocket
 * meets the >=2-user threshold on the backend.
 */
export function TrendingPockets({ onApply }) {
  const [pockets, setPockets] = useState([]);
  const [hovered, setHovered] = useState(null);
  const chipRefs = useRef({});

  useEffect(() => {
    api.get("/saved-searches/trending").then((r) => setPockets(r.data || [])).catch(() => {});
  }, []);

  if (!pockets.length) return null;

  return (
    <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="trending-pockets">
      <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
        <Flame size={11} /> Trending pockets
      </span>
      {pockets.map((p, i) => {
        const refKey = `${p.genre}-${p.bpm}-${p.key}`;
        if (!chipRefs.current[refKey]) chipRefs.current[refKey] = { current: null };
        const ref = chipRefs.current[refKey];
        return (
          <button
            key={refKey}
            ref={(el) => { ref.current = el; }}
            type="button"
            onClick={() => onApply?.(p)}
            onMouseEnter={() => setHovered(i)}
            onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
            onFocus={() => setHovered(i)}
            onBlur={() => setHovered((h) => (h === i ? null : h))}
            data-testid={`trending-pocket-${i}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/40 bg-emerald-300/5 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-200 transition-colors hover:border-emerald-300 hover:bg-emerald-300/15"
            title={`${p.count} artist${p.count === 1 ? "" : "s"} saved this pocket`}
          >
            {p.label}
            <span className="font-mono text-[9px] font-normal normal-case tracking-normal text-emerald-300/80">
              · {p.count} saved
            </span>
          </button>
        );
      })}
      {hovered !== null && pockets[hovered] && (
        <PocketPreview
          anchorRef={chipRefs.current[`${pockets[hovered].genre}-${pockets[hovered].bpm}-${pockets[hovered].key}`]}
          search={pockets[hovered]}
          onClose={() => setHovered(null)}
        />
      )}
    </div>
  );
}
